import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, X, Plus, Home } from 'lucide-react';
import { siteService } from '../../api/siteService';
import { productService } from '../../api/productService';
import '../Common/Common.css';

/**
 * Manage which catalogue products appear on a vitrine site.
 *  - left  : search the catalogue and add products
 *  - right : the site's assigned products — reorder, feature on home, remove
 * Saving replaces the whole assignment set (sync) in one call.
 */
function SiteProductManager({ site, onClose }) {
  const [assigned, setAssigned] = useState([]); // [{id, name, sku, main_image, price, show_on_home, position}]
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const assignedIds = new Set(assigned.map((p) => p.id));

  useEffect(() => {
    siteService.getSite(site.id)
      .then((data) => setAssigned(
        (data.products || []).sort((a, b) => a.position - b.position)
      ))
      .catch(() => setAssigned([]))
      .finally(() => setLoading(false));
  }, [site.id]);

  const runSearch = useCallback(async (term) => {
    setSearching(true);
    try {
      const res = await productService.getProducts({ search: term, per_page: 20, is_active: true });
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, runSearch]);

  const addProduct = (p) => {
    if (assignedIds.has(p.id)) return;
    setAssigned((prev) => [...prev, {
      id: p.id,
      name: p.name,
      sku: p.sku,
      price: p.price,
      main_image: p.main_image,
      show_on_home: false,
      position: prev.length,
    }]);
    setSaved(false);
  };

  const removeProduct = (id) => {
    setAssigned((prev) => prev.filter((p) => p.id !== id));
    setSaved(false);
  };

  const toggleHome = (id) => {
    setAssigned((prev) => prev.map((p) => p.id === id ? { ...p, show_on_home: !p.show_on_home } : p));
    setSaved(false);
  };

  const move = (index, dir) => {
    setAssigned((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = assigned.map((p, i) => ({
        product_id: p.id,
        position: i,
        show_on_home: p.show_on_home,
      }));
      await siteService.syncProducts(site.id, payload);
      setSaved(true);
    } catch (err) {
      alert('Échec de l\'enregistrement : ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const thumb = (url) => (
    url
      ? <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
      : <div style={{ width: 40, height: 40, borderRadius: 4, background: '#edf2f7', flexShrink: 0 }} />
  );

  if (loading) return <div className="loading">Chargement…</div>;

  const cell = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #edf2f7', borderRadius: 6, marginBottom: 6 };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Catalogue search */}
        <div>
          <h4 style={{ margin: '0 0 10px', color: '#2d3748' }}>Catalogue</h4>
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher un produit (nom, SKU)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {searching && <div style={{ color: '#718096', fontSize: 14 }}>Recherche…</div>}
            {!searching && results.length === 0 && (
              <div style={{ color: '#a0aec0', fontSize: 14 }}>Aucun produit</div>
            )}
            {results.map((p) => {
              const already = assignedIds.has(p.id);
              return (
                <div key={p.id} style={cell}>
                  {thumb(p.main_image)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#718096' }}>{p.sku} · {parseFloat(p.price).toFixed(2)} DH</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ padding: '4px 10px', fontSize: 13 }}
                    disabled={already}
                    onClick={() => addProduct(p)}
                  >
                    {already ? 'Ajouté' : <><Plus size={14} /> Ajouter</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Assigned products */}
        <div>
          <h4 style={{ margin: '0 0 10px', color: '#2d3748' }}>
            Produits du site ({assigned.length})
          </h4>
          <div style={{ fontSize: 12, color: '#718096', marginBottom: 12 }}>
            <Home size={12} style={{ verticalAlign: 'middle' }} /> = mis en avant sur la page d'accueil du site.
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {assigned.length === 0 && (
              <div style={{ color: '#a0aec0', fontSize: 14 }}>Aucun produit assigné</div>
            )}
            {assigned.map((p, i) => (
              <div key={p.id} style={cell}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <button type="button" className="icon-btn" onClick={() => move(i, -1)} disabled={i === 0} title="Monter">
                    <ChevronUp size={14} />
                  </button>
                  <button type="button" className="icon-btn" onClick={() => move(i, 1)} disabled={i === assigned.length - 1} title="Descendre">
                    <ChevronDown size={14} />
                  </button>
                </div>
                {thumb(p.main_image)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#718096' }}>{p.sku}</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12 }} title="Afficher sur la page d'accueil">
                  <input type="checkbox" checked={p.show_on_home} onChange={() => toggleHome(p.id)} />
                  <Home size={14} />
                </label>
                <button type="button" className="icon-btn icon-btn--danger" onClick={() => removeProduct(p.id)} title="Retirer">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="form-actions" style={{ marginTop: 20 }}>
        <button type="button" onClick={onClose} className="btn btn-secondary">Fermer</button>
        <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? 'Enregistrement…' : saved ? 'Enregistré ✓' : 'Enregistrer les produits'}
        </button>
      </div>
    </div>
  );
}

export default SiteProductManager;
